import { useCallback, useEffect, useRef } from 'react'
import type { PointerEvent as ReactPointerEvent, ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '@/lib/utils'

/**
 * Bir nechta modal ustma-ust ochilishi mumkin (masalan bloger kartochkasi ustida
 * rad etish sababi oynasi). Faqat ENG TEPADAGI modal Escape/Tab'ni ushlab qolishi
 * kerak — aks holda pastdagi modal ham birga yopilib ketadi yoki fokus tuzog'i
 * to'qnashadi. Shu uchun ochiq modallar moduldagi umumiy stekda kuzatiladi.
 */
const openModalStack: symbol[] = []

export interface ModalProps {
  open: boolean
  onClose: () => void
  children: ReactNode
  /** Skrin-riderlar uchun oyna nomi — ko'rinadigan sarlavha `children` ichida bo'lsa ham kerak. */
  ariaLabel: string
  /** `default` — 560px (kartochkalar), `compact` — 420px (tasdiqlash/tanlov oynalari). */
  size?: 'default' | 'compact'
  /** Ustma-ust modal ochilganda tepadagisi kattaroq qiymat olishi kerak. */
  zIndex?: 40 | 50
}

/**
 * Umumiy modal/bottom-sheet qobig'i (`docs/04-ui-ux.md` "Modal (kartochka ochilganda)"):
 * desktopda markazdagi oyna (`max-width: 560px`), mobilda pastdan chiqadigan panel (90vh,
 * pastga surib yopiladi). Fokus tuzog'i, Escape bilan yopish va fon skrollini qulflash
 * shu komponentda — barcha modal turlari (kartochka, tasdiqlash, sabab tanlash) shu orqali quriladi.
 *
 * Tarkib joylashuvi (header/scroll body/footer) chaqiruvchiga qoldirilgan — bu yerda faqat
 * panel, fon va sudrash ushlagichi bor. Shunda "asosiy amallar pastda yopishib turadi" qoidasi
 * chaqiruvchi tomonda oddiy `shrink-0` footer bilan ta'minlanadi, scroll konteyneri esa orada.
 */
export function Modal({
  open,
  onClose,
  children,
  ariaLabel,
  size = 'default',
  zIndex = 40,
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement | null>(null)
  const dragStartYRef = useRef<number | null>(null)
  const idRef = useRef<symbol>(Symbol('modal'))

  // Fon skrolini qulflash — modal ochiq paytida orqadagi sahifa harakatlanmasin.
  useEffect(() => {
    if (!open) return
    const original = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = original
    }
  }, [open])

  // Ochiq modallar stekiga qo'shilish/chiqarilish — Escape/Tab faqat eng tepadagisida ishlasin.
  useEffect(() => {
    if (!open) return
    const id = idRef.current
    openModalStack.push(id)
    return () => {
      const index = openModalStack.indexOf(id)
      if (index !== -1) openModalStack.splice(index, 1)
    }
  }, [open])

  // Fokus tuzog'i: ochilganda birinchi bosiladigan elementga fokus, Tab panel ichida aylanadi,
  // Escape yopadi, yopilganda avvalgi fokus qaytariladi.
  useEffect(() => {
    if (!open) return
    const panel = panelRef.current
    if (!panel) return
    const previouslyFocused = document.activeElement as HTMLElement | null

    const getFocusable = (): HTMLElement[] =>
      Array.from(
        panel.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => el.offsetParent !== null)

    const first = getFocusable()[0]
    ;(first ?? panel).focus()

    function handleKeyDown(event: KeyboardEvent) {
      // Faqat stekning tepasidagi modal reaksiya bildiradi.
      if (openModalStack[openModalStack.length - 1] !== idRef.current) return

      if (event.key === 'Escape') {
        event.preventDefault()
        onClose()
        return
      }

      if (event.key !== 'Tab') return
      const items = getFocusable()
      const firstEl = items[0]
      const lastEl = items[items.length - 1]
      if (!firstEl || !lastEl) return

      if (event.shiftKey && document.activeElement === firstEl) {
        event.preventDefault()
        lastEl.focus()
      } else if (!event.shiftKey && document.activeElement === lastEl) {
        event.preventDefault()
        firstEl.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      if (previouslyFocused && document.contains(previouslyFocused)) {
        previouslyFocused.focus()
      }
    }
  }, [open, onClose])

  // Mobilda ushlagichni pastga sudrab yopish. Faqat shu tutqichda ishlaydi — tarkib
  // ichidagi oddiy skroll bilan chalkashmasin.
  const onDragPointerDown = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    dragStartYRef.current = event.clientY
    event.currentTarget.setPointerCapture(event.pointerId)
  }, [])

  const onDragPointerMove = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    if (dragStartYRef.current === null || !panelRef.current) return
    const delta = event.clientY - dragStartYRef.current
    if (delta > 0) {
      panelRef.current.style.transform = `translateY(${delta}px)`
    }
  }, [])

  const onDragPointerUp = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const panel = panelRef.current
      const startY = dragStartYRef.current
      dragStartYRef.current = null
      if (startY === null || !panel) return

      const delta = event.clientY - startY
      panel.style.transition = 'transform 150ms ease-out'
      if (delta > 80) {
        panel.style.transform = 'translateY(100%)'
        window.setTimeout(onClose, 150)
      } else {
        panel.style.transform = ''
      }
      window.setTimeout(() => {
        if (panelRef.current) panelRef.current.style.transition = ''
      }, 160)
    },
    [onClose],
  )

  if (!open) return null

  return createPortal(
    <div className="fixed inset-0 flex items-end justify-center sm:items-center" style={{ zIndex }}>
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        tabIndex={-1}
        className={cn(
          // `focus-visible:border-brand` — panelning o'zi (`tabIndex={-1}`) faqat ichida
          // bosiladigan element bo'lmaganda fokus oladi (himoya sifatida); shu holatda ham
          // fokus butunlay ko'rinmas qolmasin uchun chegara rangi orqali belgilanadi
          // (boshqa inputlardagi bilan bir xil naqsh).
          'relative flex max-h-[90vh] w-full flex-col overflow-hidden rounded-t-2xl border border-app-border bg-app-card outline-none focus-visible:border-brand sm:max-h-[85vh] sm:rounded-card',
          size === 'default' ? 'sm:max-w-[560px]' : 'sm:max-w-[420px]',
        )}
      >
        <div
          className="flex shrink-0 touch-none justify-center py-2 sm:hidden"
          onPointerDown={onDragPointerDown}
          onPointerMove={onDragPointerMove}
          onPointerUp={onDragPointerUp}
        >
          <span className="h-1.5 w-10 rounded-full bg-app-border" aria-hidden="true" />
        </div>
        {children}
      </div>
    </div>,
    document.body,
  )
}
